import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Code, AlertTriangle, Plus, Move } from "lucide-react"
import type { JSONSchema } from "@/api/types/schema"
import { useSchemaState } from "@/hooks/schema/useSchemaState"
import { useSchemaValidation } from "@/hooks/schema/useSchemaValidation"
import { SectionEditor } from "./SectionEditor"
import { SchemaPreview } from "./SchemaPreview"
import type { ConfigComponentProps } from '../../ConfigForm'

export function SchemaEditor({ 
  config, 
  onUpdate, 
  isLoading 
}: ConfigComponentProps): JSX.Element {
  const currentSchema = config?.prompt_config?.monitor?.json_schema as unknown as JSONSchema
  
  const onSchemaChange = (schema: JSONSchema) => {
    const currentMonitor = config?.prompt_config?.monitor
    onUpdate({
      prompt_config: {
        ...config?.prompt_config,
        monitor: {
          prompt_text: currentMonitor?.prompt_text || '',
          service_type: currentMonitor?.service_type || 'LLM_SONNET35',
          json_schema: schema as unknown as Record<string, unknown>
        }
      }
    })
  }
  const {
    generatorState,
    sortedSections,
    generatedSchema,
    handleDialogTitleChange,
    handleSectionChange,
    addSection,
    removeSection,
    duplicateSection,
    addScore,
    removeScore,
    updateScore
  } = useSchemaState({ currentSchema, onSchemaChange })

  const validation = useSchemaValidation(generatorState)

  return (
    <div className="space-y-4">
      {/* Validation Errors */}
      {validation.errors.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1">
              <div className="font-medium">Schema validation errors:</div>
              <ul className="list-disc list-inside text-sm space-y-1">
                {validation.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Schema Builder */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5 text-blue-500" />
            Schema Builder
          </CardTitle>
          <CardDescription>
            Configure the structure of your analysis schema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="builder" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="builder">Builder</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>
            
            <TabsContent value="builder" className="space-y-4">
              {/* Dialog Title Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Schema Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Label>Dialog Title</Label>
                    <Input
                      value={generatorState.dialogTitle}
                      onChange={(e) => handleDialogTitleChange(e.target.value)}
                      placeholder="Analysis Results"
                      disabled={isLoading}
                    />
                    <p className="text-sm text-muted-foreground">
                      This title will appear in the AI response and in the UI
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Sections */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Move className="h-5 w-5 text-blue-500" />
                      Analysis Sections ({sortedSections.length})
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addSection}
                      disabled={isLoading}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Section
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {sortedSections.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Move className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No analysis sections configured</p>
                      <p className="text-sm">Add sections to define your schema structure</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sortedSections.map((section) => (
                        <SectionEditor
                          key={section.uuid}
                          section={section}
                          onSectionChange={handleSectionChange}
                          onDuplicate={duplicateSection}
                          onRemove={removeSection}
                          onAddScore={addScore}
                          onRemoveScore={removeScore}
                          onUpdateScore={updateScore}
                          isLoading={isLoading}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="preview" className="space-y-4">
              <SchemaPreview
                schema={generatedSchema}
                validation={validation}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
